import React, { useState } from 'react';
import { auth, db } from '../firebaseConfig';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, getDoc, collection } from 'firebase/firestore';
import { useNavigate, Link } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { isIOS, isAndroid } from '../utils/platformUtils';
import { useError } from '../contexts/ErrorContext';
import { validateEmail, validatePassword, validatePasswordConfirmation, validateName, validatePhone } from '../utils/validationUtils';
import PlatformLayout from './PlatformLayout';
import PlatformInput from './PlatformInput';
import PlatformButton from './PlatformButton';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from '../hooks/useTranslation';
import styles from './OrganizationRegistration.module.css';


function generateOrganizationCode() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

const OrganizationRegistration = () => {
  const navigate = useNavigate();
  const isNative = Capacitor.isNativePlatform();
  const { t: tAuth } = useTranslation('auth');
  const { t: tForms } = useTranslation('forms');
  const { showErrorToast } = useError();
  const [formData, setFormData] = useState({
    organizationName: '',
    businessOwnerEmail: '',
    businessOwnerPassword: '',
    confirmPassword: '',
    phoneNumber: '',
    businessOwnerFullName: '',
    termsAccepted: false
  });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({...formData, [name]: value});
    setError(''); // Clear error when user starts typing
    setFieldErrors(prev => ({ ...prev, [name]: '' })); // Clear field error
    
    // Real-time validation for specific fields
    if (name === 'businessOwnerEmail' && value) {
      const emailValidation = validateEmail(value);
      if (!emailValidation.isValid) {
        setFieldErrors(prev => ({ ...prev, businessOwnerEmail: emailValidation.error }));
      }
    }
    
    if (name === 'businessOwnerPassword' && value) {
      const passwordValidation = validatePassword(value);
      if (!passwordValidation.isValid) {
        setFieldErrors(prev => ({ ...prev, businessOwnerPassword: passwordValidation.error }));
      }
    }
    
    if (name === 'businessOwnerFullName' && value) {
      const nameValidation = validateName(value, 'Full Name');
      if (!nameValidation.isValid) {
        setFieldErrors(prev => ({ ...prev, businessOwnerFullName: nameValidation.error }));
      }
    }
    
    if (name === 'organizationName' && value) {
      const orgNameValidation = validateName(value, 'Organization Name', { allowAlphanumeric: true });
      if (!orgNameValidation.isValid) {
        setFieldErrors(prev => ({ ...prev, organizationName: orgNameValidation.error }));
      }
    }
    
    if (name === 'phoneNumber' && value) {
      const phoneValidation = validatePhone(value);
      if (!phoneValidation.isValid) {
        setFieldErrors(prev => ({ ...prev, phoneNumber: phoneValidation.error }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setFieldErrors({});
    setSuccess(null);

    // Comprehensive validation using validationUtils
    const emailValidation = validateEmail(formData.businessOwnerEmail);
    const passwordValidation = validatePassword(formData.businessOwnerPassword);
    const passwordConfirmationValidation = validatePasswordConfirmation(formData.businessOwnerPassword, formData.confirmPassword);
    const nameValidation = validateName(formData.businessOwnerFullName, 'Full Name');
    const orgNameValidation = validateName(formData.organizationName, 'Organization Name', { allowAlphanumeric: true });
    const phoneValidation = validatePhone(formData.phoneNumber);
    
    // Collect all validation errors
    const newFieldErrors = {};
    let hasErrors = false;
    
    if (!emailValidation.isValid) {
      newFieldErrors.businessOwnerEmail = emailValidation.error;
      hasErrors = true;
    }
    
    if (!passwordValidation.isValid) {
      newFieldErrors.businessOwnerPassword = passwordValidation.error;
      hasErrors = true;
    }
    
    if (!passwordConfirmationValidation.isValid) {
      newFieldErrors.confirmPassword = passwordConfirmationValidation.error;
      hasErrors = true;
    }
    
    if (!nameValidation.isValid) {
      newFieldErrors.businessOwnerFullName = nameValidation.error;
      hasErrors = true;
    }
    
    if (!orgNameValidation.isValid) {
      newFieldErrors.organizationName = orgNameValidation.error;
      hasErrors = true;
    }
    
    if (!phoneValidation.isValid) {
      newFieldErrors.phoneNumber = phoneValidation.error;
      hasErrors = true;
    }
    
    // Validate Terms acceptance
    if (!formData.termsAccepted) {
      newFieldErrors.termsAccepted = tAuth('legal.acceptTermsRequired');
      hasErrors = true;
    }
    
    if (hasErrors) {
      setFieldErrors(newFieldErrors);
      setError(tForms('messages.fixErrorsBelow'));
      setLoading(false);
      return;
    }

    try {
      // 1. Create auth account for business owner
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        formData.businessOwnerEmail, 
        formData.businessOwnerPassword
      );
      const userId = userCredential.user.uid;

      // 2. Generate and verify unique organization code
      let organizationCode;
      let isUnique = false;
      let attempts = 0;
      const MAX_ATTEMPTS = 10;

      while (!isUnique && attempts < MAX_ATTEMPTS) {
        organizationCode = generateOrganizationCode();
        const orgCodeRef = doc(db, 'organizationCodes', organizationCode);
        const orgCodeDoc = await getDoc(orgCodeRef);
        
        if (!orgCodeDoc.exists()) {
          isUnique = true;
        }
        attempts++;
      }

      if (!isUnique) {
        showErrorToast(tAuth('organizationRegistration.error.failedToGenerateOrgCode'), {
          variant: 'error',
          autoHide: true,
          duration: 4000
        });
        return;
      }

      // 3. Create organization document
      const organizationRef = doc(collection(db, 'organizations'));
      const organizationId = organizationRef.id;

      // First create the organization document
      const createdAt = new Date().toISOString();
      const trialStartDate = createdAt;
      const trialEndDate = new Date(new Date(createdAt).getTime() + (30 * 24 * 60 * 60 * 1000)).toISOString();
      
      await setDoc(organizationRef, {
        name: formData.organizationName,
        email: formData.businessOwnerEmail,
        phoneNumber: formData.phoneNumber,
        organizationCode,
        approved: true, // TEMPORARY: Auto-approve organizations
        approvedAt: new Date().toISOString(), // Add approval timestamp
        createdAt,
        subscriptionStatus: 'trial',
        trialStartDate,
        trialEndDate,
        subscriptionPlan: null,
        subscriptionStartDate: null,
        paymentGatewayId: null,
        lastPaymentDate: null,
        nextBillingDate: null,
        trialNotificationsSent: {
          day7: false,
          day3: false,
          day1: false,
          expired: false
        }
      });

      // Verify organization document was created
      const orgCheck = await getDoc(organizationRef);
      if (!orgCheck.exists()) {
        showErrorToast(tAuth('organizationRegistration.error.failedToCreateOrganization'), {
          variant: 'error',
          autoHide: true,
          duration: 4000
        });
        return;
      }

      // 4. Create organization code lookup
      const orgCodeRef = doc(db, 'organizationCodes', organizationCode);
      await setDoc(orgCodeRef, {
        organizationId
      });

      // Verify organization code document was created
      const orgCodeCheck = await getDoc(orgCodeRef);
      if (!orgCodeCheck.exists()) {
        showErrorToast(tAuth('organizationRegistration.error.failedToCreateOrgCode'), {
          variant: 'error',
          autoHide: true,
          duration: 4000
        });
        return;
      }

      // 5. Create business owner user document in the root users collection
      const acceptedAt = new Date().toISOString();
      const userRef = doc(db, 'users', userId);
      await setDoc(userRef, {
        email: formData.businessOwnerEmail,
        fullName: formData.businessOwnerFullName,
        organizationId: organizationId,
        createdAt: acceptedAt,
        termsAcceptedAt: acceptedAt,
        privacyAcceptedAt: acceptedAt
      });

      // Verify the user document was created
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) {
        showErrorToast(tAuth('organizationRegistration.error.failedToCreateUserProfile'), {
          variant: 'error',
          autoHide: true,
          duration: 4000
        });
        return;
      }

      // 6. Create business owner user document in organization's users collection
      const orgUserRef = doc(db, `organizations/${organizationId}/users/${userId}`);
      await setDoc(
        orgUserRef,
        {
          email: formData.businessOwnerEmail,
          fullName: formData.businessOwnerFullName,
          role: 'BusinessOwner',
          approved: true,
          createdAt: acceptedAt,
          termsAcceptedAt: acceptedAt,
          privacyAcceptedAt: acceptedAt
        }
      );

      // Verify the organization user document was created
      const orgUserDoc = await getDoc(orgUserRef);
      if (!orgUserDoc.exists()) {
        showErrorToast(tAuth('organizationRegistration.error.failedToCreateOrgUserProfile'), {
          variant: 'error',
          autoHide: true,
          duration: 4000
        });
        return;
      }

      // Update totalUsers count in organization
      try {
        const { updateOrganizationUserCount } = await import('../services/userCountService');
        await updateOrganizationUserCount(organizationId);
      } catch (err) {
        // Silent error handling for user count update
      }

      // Double-check organization code document
      const orgCodeDoc = await getDoc(doc(db, 'organizationCodes', organizationCode));
      if (!orgCodeDoc.exists()) {
        showErrorToast('Failed to create organization code. Please try again.', {
          variant: 'error',
          autoHide: true,
          duration: 4000
        });
        return;
      }

      // Now sign out after all documents are created
      await auth.signOut();

      // Navigate to login page with success message
      navigate('/login', { 
        state: { 
          message: `Organization registered successfully!\n\nYour organization code is: ${organizationCode}. Please save this code - you'll need it to share with your employees.\n\nYour organization has been approved and you can now log in immediately.`,
          isOrgCode: true
        }
      });
      
      // Clear the form
      setFormData({
        organizationName: '',
        businessOwnerEmail: '',
        businessOwnerPassword: '',
        confirmPassword: '',
        phoneNumber: '',
        businessOwnerFullName: '',
        termsAccepted: false
      });

    } catch (error) {
      setError(tAuth('organizationRegistration.error.errorDuringRegistration'));
      
      // Only sign out if there was an actual error during registration
      if (error.code !== 'auth/email-already-in-use') {
        try {
          await auth.signOut();
        } catch (signOutError) {
          // Silent cleanup error
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Render different layouts based on platform
  if (isNative) {
    return (
      <PlatformLayout>
        <div className={styles.formContainer}>
          <div className={`${styles.formCard} fade-in`}>
            <div style={{ marginBottom: '32px' }}>
              <h1 className={styles.formTitle}>{tForms('organization.title')}</h1>
              <p className={styles.formSubtitle}>{tForms('organization.subtitle')}</p>
            </div>

            <form onSubmit={handleSubmit} style={{ width: '100%' }} className={`${styles.formWrapper} form-simple`}>
              {error && (
                <div style={{
                  backgroundColor: isIOS() ? '#FFE5E5' : isAndroid() ? '#FFEBEE' : '#FFEBEE',
                  color: isIOS() ? '#FF3B30' : isAndroid() ? '#B00020' : '#D32F2F',
                  padding: '12px 16px',
                  borderRadius: isIOS() ? '10px' : isAndroid() ? '4px' : '8px',
                  marginBottom: '16px',
                  fontSize: '14px',
                  border: `1px solid ${isIOS() ? '#FF3B30' : isAndroid() ? '#B00020' : '#D32F2F'}`
                }}>
                  {error}
                </div>
              )}

              <div style={{ marginBottom: '16px' }}>
                <PlatformInput
                  type="text"
                  name="organizationName"
                  placeholder={tForms('labels.organizationName')}
                  value={formData.organizationName}
                  onChange={handleChange}
                  required
                />
                {fieldErrors.organizationName && (
                  <div style={{
                    color: isIOS() ? '#FF3B30' : isAndroid() ? '#B00020' : '#D32F2F',
                    fontSize: '12px',
                    marginTop: '4px',
                    marginLeft: '4px'
                  }}>
                    {fieldErrors.organizationName}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '16px' }}>
                <PlatformInput
                  type="text"
                  name="businessOwnerFullName"
                  placeholder={tForms('labels.fullName')}
                  value={formData.businessOwnerFullName}
                  onChange={handleChange}
                  required
                />
                {fieldErrors.businessOwnerFullName && (
                  <div style={{
                    color: isIOS() ? '#FF3B30' : isAndroid() ? '#B00020' : '#D32F2F',
                    fontSize: '12px',
                    marginTop: '4px',
                    marginLeft: '4px'
                  }}>
                    {fieldErrors.businessOwnerFullName}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '16px' }}>
                <PlatformInput
                  type="email"
                  name="businessOwnerEmail"
                  placeholder={tForms('placeholders.email')}
                  value={formData.businessOwnerEmail}
                  onChange={handleChange}
                  required
                />
                {fieldErrors.businessOwnerEmail && (
                  <div style={{
                    color: isIOS() ? '#FF3B30' : isAndroid() ? '#B00020' : '#D32F2F',
                    fontSize: '12px',
                    marginTop: '4px',
                    marginLeft: '4px'
                  }}>
                    {fieldErrors.businessOwnerEmail}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '16px' }}>
                <PlatformInput
                  type="tel"
                  name="phoneNumber"
                  placeholder={tForms('organization.phoneNumber')}
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  required
                />
                {fieldErrors.phoneNumber && (
                  <div style={{
                    color: isIOS() ? '#FF3B30' : isAndroid() ? '#B00020' : '#D32F2F',
                    fontSize: '12px',
                    marginTop: '4px',
                    marginLeft: '4px'
                  }}>
                    {fieldErrors.phoneNumber}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '16px' }}>
                <PlatformInput
                  type="password"
                  name="businessOwnerPassword"
                  placeholder={tForms('placeholders.password')}
                  value={formData.businessOwnerPassword}
                  onChange={handleChange}
                  required
                />
                {fieldErrors.businessOwnerPassword && (
                  <div style={{
                    color: isIOS() ? '#FF3B30' : isAndroid() ? '#B00020' : '#D32F2F',
                    fontSize: '12px',
                    marginTop: '4px',
                    marginLeft: '4px'
                  }}>
                    {fieldErrors.businessOwnerPassword}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '16px' }}>
                <PlatformInput
                  type="password"
                  name="confirmPassword"
                  placeholder={tForms('placeholders.confirmPassword')}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                />
                {fieldErrors.confirmPassword && (
                  <div style={{
                    color: isIOS() ? '#FF3B30' : isAndroid() ? '#B00020' : '#D32F2F',
                    fontSize: '12px',
                    marginTop: '4px',
                    marginLeft: '4px'
                  }}>
                    {fieldErrors.confirmPassword}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: isIOS() ? '#000000' : isAndroid() ? '#212121' : '#333333',
                  lineHeight: '1.5'
                }}>
                  <input
                    type="checkbox"
                    name="termsAccepted"
                    checked={formData.termsAccepted}
                    onChange={(e) => {
                      setFormData({...formData, termsAccepted: e.target.checked});
                      setFieldErrors(prev => ({ ...prev, termsAccepted: '' }));
                    }}
                    style={{
                      marginTop: '2px',
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      accentColor: isIOS() ? '#007AFF' : isAndroid() ? '#007AFF' : '#1976D2',
                      flexShrink: 0
                    }}
                  />
                  <span style={{ flex: 1 }}>
                    {tAuth('legal.acceptTerms')}{' '}
                    <Link
                      to="/terms-of-service"
                      style={{
                        color: isIOS() ? '#007AFF' : isAndroid() ? '#007AFF' : '#1976D2',
                        textDecoration: 'underline',
                        fontWeight: '600'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {tAuth('legal.termsLink')}
                    </Link>
                    {' '}{tAuth('legal.acceptTermsAnd')}{' '}
                    <Link
                      to="/privacy-policy"
                      style={{
                        color: isIOS() ? '#007AFF' : isAndroid() ? '#007AFF' : '#1976D2',
                        textDecoration: 'underline',
                        fontWeight: '600'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {tAuth('legal.privacyLink')}
                    </Link>
                  </span>
                </label>
                {fieldErrors.termsAccepted && (
                  <div style={{
                    color: isIOS() ? '#FF3B30' : isAndroid() ? '#B00020' : '#D32F2F',
                    fontSize: '12px',
                    marginTop: '4px',
                    marginLeft: '30px'
                  }}>
                    {fieldErrors.termsAccepted}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '2px' }}>
                <PlatformButton
                  type="submit"
                  disabled={loading}
                  style={{ width: '100%' }}
                >
                  {loading ? tForms('organization.creatingOrganization') : tForms('organization.createOrganization')}
                </PlatformButton>
              </div>

              <div style={{ textAlign: 'center' }}>
                <span style={{ color: isIOS() ? '#8E8E93' : isAndroid() ? '#757575' : '#666666' }}>
                  {tAuth('register.alreadyHaveAccount')}{' '}
                </span>
                <Link
                  to="/login"
                  style={{
                    color: isIOS() ? '#007AFF' : isAndroid() ? '#007AFF' : '#1976D2',
                    textDecoration: 'none',
                    fontWeight: '600'
                  }}
                >
                  {tAuth('register.signIn')}
                </Link>
              </div>
            </form>
          </div>
        </div>
      </PlatformLayout>
    );
  }

  // Web version (existing code)
  return (
    <div className="container">
      <div className={!isNative ? "auth-container" : ""}>
        {/* Language Switcher - Subtle placement for web */}
        <LanguageSwitcher 
          variant="toggle" 
          size="small" 
          position="top-right"
        />
        
        <div className="auth-header">
          <h1>{tForms('organization.title')}</h1>
          <p className="auth-subtitle">{tForms('organization.subtitle')}</p>
        </div>
        
        <form onSubmit={handleSubmit} className={`${styles.formWrapper} auth-form form-simple`}>
          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}

          {success && (
            <div className="org-code-message" style={{ 
              backgroundColor: '#e8f5e9',
              color: '#2e7d32',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1rem',
              whiteSpace: 'pre-line',
              border: '1px solid #81c784'
            }}>
              {success.message}
            </div>
          )}

          <div className={styles.field}>
            <label htmlFor="organizationName">{tForms('labels.organizationName')}</label>
            <input
              type="text"
              id="organizationName"
              name="organizationName"
              value={formData.organizationName}
              onChange={handleChange}
              placeholder={tForms('placeholders.organizationName')}
              required
              disabled={loading}
              className={fieldErrors.organizationName ? 'error' : ''}
            />
            {fieldErrors.organizationName && (
              <div className="field-error">{fieldErrors.organizationName}</div>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="businessOwnerFullName">{tForms('labels.fullName')}</label>
            <input
              type="text"
              id="businessOwnerFullName"
              name="businessOwnerFullName"
              value={formData.businessOwnerFullName}
              onChange={handleChange}
              placeholder={tForms('placeholders.fullName')}
              required
              disabled={loading}
              className={fieldErrors.businessOwnerFullName ? 'error' : ''}
            />
            {fieldErrors.businessOwnerFullName && (
              <div className="field-error">{fieldErrors.businessOwnerFullName}</div>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="businessOwnerEmail">{tForms('labels.email')}</label>
            <input
              type="email"
              id="businessOwnerEmail"
              name="businessOwnerEmail"
              value={formData.businessOwnerEmail}
              onChange={handleChange}
              placeholder={tForms('placeholders.email')}
              required
              disabled={loading}
              className={fieldErrors.businessOwnerEmail ? 'error' : ''}
            />
            {fieldErrors.businessOwnerEmail && (
              <div className="field-error">{fieldErrors.businessOwnerEmail}</div>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="phoneNumber">{tForms('organization.phoneNumber')}</label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              placeholder={tForms('placeholders.phoneNumber')}
              required
              disabled={loading}
              className={fieldErrors.phoneNumber ? 'error' : ''}
            />
            {fieldErrors.phoneNumber && (
              <div className="field-error">{fieldErrors.phoneNumber}</div>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="businessOwnerPassword">{tForms('labels.password')}</label>
            <input
              type="password"
              id="businessOwnerPassword"
              name="businessOwnerPassword"
              value={formData.businessOwnerPassword}
              onChange={handleChange}
              placeholder={tForms('placeholders.password')}
              required
              disabled={loading}
              minLength="6"
              className={fieldErrors.businessOwnerPassword ? 'error' : ''}
            />
            {fieldErrors.businessOwnerPassword && (
              <div className="field-error">{fieldErrors.businessOwnerPassword}</div>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="confirmPassword">{tForms('labels.confirmPassword')}</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder={tForms('placeholders.confirmPassword')}
              required
              disabled={loading}
              minLength="6"
              className={fieldErrors.confirmPassword ? 'error' : ''}
            />
            {fieldErrors.confirmPassword && (
              <div className="field-error">{fieldErrors.confirmPassword}</div>
            )}
          </div>

          <div className={styles.field}>
            <label style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75rem',
              cursor: 'pointer',
              fontSize: '0.875rem',
              color: 'var(--text-primary)',
              lineHeight: '1.5',
              marginBottom: fieldErrors.termsAccepted ? '0.25rem' : '0'
            }}>
              <input
                type="checkbox"
                id="termsAccepted"
                name="termsAccepted"
                checked={formData.termsAccepted}
                onChange={(e) => {
                  setFormData({...formData, termsAccepted: e.target.checked});
                  setFieldErrors(prev => ({ ...prev, termsAccepted: '' }));
                }}
                style={{
                  marginTop: '2px',
                  width: '18px',
                  height: '18px',
                  cursor: 'pointer',
                  accentColor: 'var(--primary-color)',
                  flexShrink: 0
                }}
              />
              <span style={{ flex: 1 }}>
                {tAuth('legal.acceptTerms')}{' '}
                <Link
                  to="/terms-of-service"
                  className="auth-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  {tAuth('legal.termsLink')}
                </Link>
                {' '}{tAuth('legal.acceptTermsAnd')}{' '}
                <Link
                  to="/privacy-policy"
                  className="auth-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  {tAuth('legal.privacyLink')}
                </Link>
              </span>
            </label>
            {fieldErrors.termsAccepted && (
              <div className="field-error" style={{ marginLeft: '1.75rem' }}>
                {fieldErrors.termsAccepted}
              </div>
            )}
          </div>

          <button 
            type="submit" 
            className={styles.submit}
            disabled={loading}
          >
            {loading ? tForms('organization.creatingOrganization') : success ? tForms('organization.organizationCreated') : tForms('organization.createOrganization')}
          </button>

          <div className="auth-footer">
            <p>
              {tAuth('register.alreadyHaveAccount')}{' '}
              <Link to="/login" className="auth-link">
                {tAuth('register.signIn')}
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrganizationRegistration; 